import { type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  CLONE_META_DETAIL_TEXT_SX,
  CLONE_META_DETAILS_LINK_SX,
  CLONE_META_VALUE_WRAPPER_SX,
  CLONE_TOOLTIP_SLOT_PROPS,
  TableMetaInlineRow,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { Box, Link, Tooltip, type Theme } from '@/ui';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '@/ui/composites/table/tableTooltip';
import type { SxProps } from '@/ui/primitives';

type TableBreakdownLineProps = {
  icon: ReactElement;
  content: ReactNode;
  ariaLabel?: string;
  href?: string | null;
  disableLinks?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  linkSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  valueWrapperSx?: SxProps<Theme>;
  rowTitle?: string;
  valueTitle?: string | null;
};

export const TableBreakdownLine = ({
  icon,
  content,
  ariaLabel,
  href = null,
  disableLinks = false,
  onClick,
  linkSx,
  contentSx,
  valueWrapperSx,
  rowTitle,
  valueTitle,
}: TableBreakdownLineProps): ReactElement => (
  <TableMetaInlineRow
    icon={
      rowTitle ? (
        <Tooltip
          title={rowTitle}
          arrow
          enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box component="span">{icon}</Box>
        </Tooltip>
      ) : (
        icon
      )
    }
    textSx={CLONE_META_DETAIL_TEXT_SX}
    rowSx={{ width: '100%', maxWidth: '100%' }}
    contentSx={contentSx}
    content={
      <Tooltip
        title={valueTitle ?? ''}
        arrow
        enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        disableHoverListener={!valueTitle}
      >
        <Box
          component="span"
          sx={[
            CLONE_META_VALUE_WRAPPER_SX,
            ...(Array.isArray(valueWrapperSx)
              ? valueWrapperSx
              : valueWrapperSx == null
                ? []
                : [valueWrapperSx]),
          ]}
        >
          {disableLinks || href === null ? (
            ariaLabel ? (
              <span aria-label={ariaLabel}>{content}</span>
            ) : (
              content
            )
          ) : (
            <Link
              component={RouterLink}
              to={href}
              underline="hover"
              color="inherit"
              onClick={onClick}
              sx={linkSx ?? CLONE_META_DETAILS_LINK_SX}
            >
              {ariaLabel ? <span aria-label={ariaLabel}>{content}</span> : content}
            </Link>
          )}
        </Box>
      </Tooltip>
    }
  />
);
